import React from 'react';
import { Reveal } from './Reveal';
import { Cpu, Layers, Network, Lock, ServerCog, Bot } from 'lucide-react';

const LAYERS = [
  {
    icon: Network,
    title: 'Orchestration Layer',
    color: '#FF6B1A',
    desc: 'Routes prompts to the right agent, retains memory across turns, chains multi-step tasks.',
    chips: ['Session memory', 'Multi-step routing', 'Fallback graph'],
  },
  {
    icon: Bot,
    title: 'Agent Mesh',
    color: '#39FF14',
    desc: '18 production-grade agents + every user fork — a compounding library of revenue playbooks.',
    chips: ['18 marketplace agents', 'No-code Builder', 'Fork & version'],
  },
  {
    icon: Cpu,
    title: 'Model Substrate',
    color: '#FFB17A',
    desc: 'GPT-5, Claude Sonnet 4.6, Gemini 3 Flash — we route around outages and price changes automatically.',
    chips: ['GPT-5', 'Claude Sonnet 4.6', 'Gemini 3 Flash', 'Auto-fallback'],
  },
  {
    icon: Layers,
    title: 'Data Plane',
    color: '#39FF14',
    desc: 'Conversations, agents, billing — all in MongoDB with usage analytics and plan-aware quotas.',
    chips: ['MongoDB', 'Per-tenant isolation', 'Usage metering'],
  },
  {
    icon: ServerCog,
    title: 'Streaming Edge',
    color: '#FF6B1A',
    desc: 'Sub-200ms first-token via SSE. Token-by-token rendering for ChatGPT-grade UX.',
    chips: ['SSE', 'Edge-buffered', 'Stateless workers'],
  },
  {
    icon: Lock,
    title: 'Trust & Security',
    color: '#39FF14',
    desc: 'Cookie sessions, idempotent billing, plan auto-downgrade, system prompts never leaked on share links.',
    chips: ['HTTP-only cookies', 'Idempotent webhooks', 'Server-side gating'],
  },
];

export const Architecture = () => {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#FF6B1A]">// architecture</span>
          <h2 className="display text-2xl md:text-4xl font-semibold mt-1 leading-tight">
            Built like infra. <span className="gradient-text">Feels like magic.</span>
          </h2>
        </div>
        <div className="text-[11px] text-white/45 mono">six layers · zero compromise</div>
      </div>
      <Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {LAYERS.map((l, i) => (
            <div key={l.title} className="card-base card-hover p-5 flex flex-col">
              <div className="h-10 w-10 rounded-xl grid place-items-center hairline mb-3" style={{ boxShadow: `0 0 16px ${l.color}33` }}>
                <l.icon className="h-5 w-5" style={{ color: l.color }} />
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-white/40">layer 0{i + 1}</div>
              <div className="display text-lg font-semibold mt-0.5">{l.title}</div>
              <p className="text-sm text-white/65 mt-2 flex-1">{l.desc}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {l.chips.map((c) => (
                  <span key={c} className="text-[10px] px-2 py-1 rounded-full hairline text-white/65 mono">{c}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Reveal>
    </section>
  );
};
